import type { Identifier } from 'react-admin'

/**
 * Many-to-many links — a document's customers and boats, an appointment's —
 * are join rows (document_customer, appointment_boat, …). react-admin 5's
 * <ReferenceArrayInput> wants a plain list of ids in the form and no longer
 * applies a format/parse to translate between the two, so a form edits an id
 * list next to the rows:
 *
 * - `withJoinIds` adds the id lists to a record before the form opens,
 * - `toJoinRows` is the Create/Edit `transform` that turns them back into join
 *   rows. Rows that stay keep their id; the data provider deletes the rest.
 */
export interface JoinField {
  /** The join rows, e.g. 'appointment_boat'. */
  rows: string
  /** The foreign key in a row, e.g. 'boat_id'. */
  key: string
  /** The id list the form edits, e.g. 'boat_ids'. */
  ids: string
}

type Row = Record<string, any>

export const withJoinIds = <T extends Row | undefined>(record: T, fields: JoinField[]): T =>
  record &&
  ({
    ...record,
    ...Object.fromEntries(
      fields.map(({ rows, key, ids }) => [
        ids,
        ((record[rows] ?? []) as Row[]).map((row) => row[key]).filter((id) => id != null),
      ])
    ),
  } as T)

export const toJoinRows =
  (fields: JoinField[]) =>
  (data: Row): Row => {
    const out = { ...data }
    for (const { rows, key, ids } of fields) {
      if (!(ids in out)) continue
      const existing = (data[rows] ?? []) as Row[]
      out[rows] = ((data[ids] ?? []) as Identifier[]).map(
        (id) => existing.find((row) => row[key] === id) ?? { [key]: id }
      )
      delete out[ids]
    }
    return out
  }
